import React, { type ReactElement, useState } from 'react'
import { Dialog, DialogActions, DialogContent, DialogTitle, Tooltip } from '@mui/material'
import dayjs, { type Dayjs } from 'dayjs'

import { Button, DateRangePanel, Icon, IconButton, Typography } from '~components/internal'

export interface AICategoriesBackfillDialogProps {
  open: boolean
  onClose: () => void
  onStart: (startDate: string, endDate: string) => void
}

const AICategoriesBackfillDialog: React.FC<AICategoriesBackfillDialogProps> = ({
  open,
  onClose,
  onStart,
}): ReactElement => {
  const [startDate, setStartDate] = useState<Dayjs | null>(dayjs().subtract(29, 'day'))
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs())

  const isEndDateBeforeStart = Boolean(startDate && endDate && endDate.isBefore(startDate, 'day'))

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle className='ai-categories-editor__dialog-title'>
        Backfill categories
        <Tooltip title='Close'>
          <IconButton aria-label='Close' size='small' onClick={onClose}>
            <Icon name='close' fontSize='small' />
          </IconButton>
        </Tooltip>
      </DialogTitle>
      <DialogContent className='ai-categories-editor__backfill'>
        <Typography variant='body2'>
          Re-categorize past interactions in the selected date range using your current saved
          categories. Interactions that already have a result for the current categories are
          skipped.
        </Typography>
        <DateRangePanel
          startDate={startDate}
          endDate={endDate}
          onChange={(start, end) => {
            setStartDate(start)
            setEndDate(end)
          }}
        />
        {isEndDateBeforeStart && (
          <Typography variant='caption'>End date must be on or after start date.</Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant='outlined' color='primary' onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant='contained'
          color='primary'
          onClick={() => {
            if (!startDate || !endDate) return
            onStart(startDate.format('YYYY-MM-DD'), endDate.format('YYYY-MM-DD'))
          }}
          disabled={!startDate || !endDate || isEndDateBeforeStart}
        >
          Start backfill
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default AICategoriesBackfillDialog
